import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminShell from '../components/layout/AdminShell';
import { CollapsibleSection, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import {
  Settings, Building2, Hash, Globe, Calendar,
  Users, Shield, Key, Info
} from 'lucide-react';

const CURRENCIES = ['USD','GBP','EUR','CAD','AUD','SGD','HKD','JPY','CHF','AED','INR'];
const DATE_FORMATS = ['DD-MMM-YYYY','DD/MM/YYYY','MM/DD/YYYY','YYYY-MM-DD'];
const TIMEZONES = [
  'UTC','America/New_York','America/Chicago','America/Denver','America/Los_Angeles',
  'America/Toronto','Europe/London','Europe/Paris','Europe/Berlin','Europe/Zurich',
  'Asia/Dubai','Asia/Singapore','Asia/Tokyo','Asia/Mumbai','Australia/Sydney',
];

export default function SystemSettingsPage() {
  const { user, systemSettings, loadUser } = useAuth();
  const { t } = useTranslation();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/system/settings');
      setSettings(res.data || {});
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load settings'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const s = (key, val) => setSettings(p => ({ ...p, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/system/settings', settings);
      toast.success('Settings saved — changes take effect on next login.');
      // Reload user to get updated systemSettings in context
      if (loadUser) await loadUser();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
    }
    setSaving(false);
  };

  if (loading) return <AdminShell><div className="py-16"><LoadingSpinner /></div></AdminShell>;

  return (
    <AdminShell>
      <div className="flex items-center justify-between pb-5 border-b mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            System Administration
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configure system-wide settings for <strong>{settings.company_name || `your ${t('term.organisation')}`}</strong>
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="system-settings-save">
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      {/* Tenant info banner */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20 mb-6">
        <Shield className="h-4 w-4 text-primary flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium">
            You are logged in as <strong>{user?.first_name} {user?.last_name}</strong> ({user?.user_type}) — Tenant Admin
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Changes made here apply to your {t('term.organisation')} only and do not affect other clients.
          </p>
        </div>
        {settings.tenant_id && (
          <Badge variant="outline" className="text-xs font-mono">{settings.tenant_id || 'eyvo-demo'}</Badge>
        )}
      </div>

      <div className="space-y-4 max-w-3xl">

        {/* ── Company Information ── */}
        <CollapsibleSection title="Company Information" defaultOpen>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5 col-span-2">
              <Label htmlFor="company-name">Company Name</Label>
              <Input id="company-name" value={settings.company_name || ''} onChange={e => s('company_name', e.target.value)} placeholder="Your company name" />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Company Address</Label>
              <Input value={settings.company_address || ''} onChange={e => s('company_address', e.target.value)} placeholder="Registered address" />
            </div>
            <div className="space-y-1.5">
              <Label>City</Label>
              <Input value={settings.company_city || ''} onChange={e => s('company_city', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Input value={settings.company_country || ''} onChange={e => s('company_country', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Company Registration Number</Label>
              <Input value={settings.registration_number || ''} onChange={e => s('registration_number', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Tax / VAT Number</Label>
              <Input value={settings.tax_id || ''} onChange={e => s('tax_id', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Support / Finance Email</Label>
              <Input type="email" value={settings.finance_email || ''} onChange={e => s('finance_email', e.target.value)} placeholder="finance@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Website</Label>
              <Input value={settings.website || ''} onChange={e => s('website', e.target.value)} placeholder="www.company.com" />
            </div>
          </div>
        </CollapsibleSection>

        {/* ── Financial Formatting ── */}
        <CollapsibleSection title="Financial & Number Formatting" defaultOpen>
          <div className="grid grid-cols-2 gap-6">
            {/* Decimal Places */}
            <div className="space-y-3">
              <div>
                <Label className="text-sm font-semibold">Decimal Places</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Number of decimal places shown on all financial figures throughout the system.
                </p>
              </div>
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4].map(dp => (
                  <button
                    key={dp}
                    onClick={() => s('decimal_places', dp)}
                    className={`w-12 h-10 rounded-lg border text-sm font-semibold transition-all ${
                      (settings.decimal_places ?? 2) === dp
                        ? 'bg-primary text-white border-primary'
                        : 'border-border hover:border-primary/50 hover:bg-accent'
                    }`}
                    aria-pressed={(settings.decimal_places ?? 2) === dp}
                    data-testid={`dp-${dp}`}
                  >
                    {dp}
                  </button>
                ))}
              </div>
              {/* Live preview */}
              <div className="rounded-lg bg-muted/30 border p-3 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Preview</p>
                {[1234567.5, 0.123, 99.9].map(n => (
                  <div key={n} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{n}</span>
                    <span className="font-mono font-semibold">
                      {new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: settings.decimal_places ?? 2,
                        maximumFractionDigits: settings.decimal_places ?? 2,
                      }).format(n)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* System Currency + Date Format */}
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">System Currency</Label>
                <p className="text-xs text-muted-foreground">Default currency for all new transactions.</p>
                <select value={settings.system_currency || 'USD'} onChange={e => s('system_currency', e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Date Display Format</Label>
                <select value={settings.date_format || 'DD-MMM-YYYY'} onChange={e => s('date_format', e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                  {DATE_FORMATS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <p className="text-xs text-muted-foreground">
                  Preview: {(() => { const t = settings.timezone || systemSettings?.timezone || 'UTC'; try { return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: t }); } catch { return new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } })()}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold">Timezone</Label>
                <select value={settings.timezone || 'UTC'} onChange={e => s('timezone', e.target.value)}
                  className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* ── Licences ── */}
        <CollapsibleSection title={t('term.licenceInfo')} defaultOpen={false}>
          <div className="grid grid-cols-3 gap-4">
            <div className="border rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-primary">{settings.license_limit ?? '—'}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('term.userLicenceLimit')}</p>
            </div>
            <div className="border rounded-xl p-4 text-center">
              <LicencedUserCount />
              <p className="text-xs text-muted-foreground mt-1">Currently Licensed Users</p>
            </div>
            <div className="border rounded-xl p-4 text-center">
              <EnabledModulesCount />
              <p className="text-xs text-muted-foreground mt-1">Active Modules</p>
            </div>
          </div>
          <div className="mt-4 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800" dangerouslySetInnerHTML={{ __html: t('term.licenceManagedMsg') }} />
          </div>
        </CollapsibleSection>

        {/* ── Coming Soon sections ── */}
        {[
          { title: 'Approval Workflow Configuration', desc: 'Define approval rules, thresholds and escalation paths.' },
          { title: 'Email & Notification Settings', desc: 'SMTP configuration, email templates, and notification preferences.' },
          { title: 'Integration Settings', desc: 'API keys, SSO (Okta/Entra), ERP connectors, and webhooks.' },
          { title: 'Document Templates', desc: 'Company letterhead, PO footer text, and document numbering sequences.' },
          { title: 'Security & Access Control', desc: 'Password policy, session timeout, IP restrictions, and MFA.' },
          { title: 'Audit & Compliance', desc: 'Audit log retention, GDPR data export, and compliance reporting.' },
        ].map(({ title, desc }) => (
          <CollapsibleSection key={title} title={title} defaultOpen={false}>
            <div className="flex items-center gap-3 py-4">
              <div className="w-8 h-8 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                <Settings className="h-4 w-4 text-muted-foreground/50" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{desc}</p>
                <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full border border-dashed border-primary/30 bg-primary/5 text-primary font-medium">Coming Soon</span>
              </div>
            </div>
          </CollapsibleSection>
        ))}
      </div>
    </AdminShell>
  );
}

// ── Small helper components ────────────────────────────────────────────────
function LicencedUserCount() {
  const [count, setCount] = useState('—');
  useEffect(() => {
    api.get('/api/users/stats/license').then(r => setCount(r.data.licensed)).catch(() => {});
  }, []);
  return <p className="text-2xl font-bold">{count}</p>;
}

function EnabledModulesCount() {
  const [count, setCount] = useState('—');
  useEffect(() => {
    api.get('/api/system/settings').then(r => setCount((r.data?.enabled_modules || []).length || 'All')).catch(() => {});
  }, []);
  return <p className="text-2xl font-bold text-green-600">{count}</p>;
}
